import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

type Props = {
  dateIso?: string | null;
  email?: string | null;
  extraLabel?: string;
  className?: string;
  children: ReactNode;
};

const SHOW_DELAY_MS = 450;
const HIDE_DELAY_MS = 120;

function preciseItalianDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" });
}

export function WaveIdentityHoverTooltip({
  dateIso,
  email,
  extraLabel,
  className = "",
  children,
}: Props) {
  const [visible, setVisible] = useState(false);
  const showTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const tooltipLines = useMemo(() => {
    const lines: string[] = [];
    const precise = preciseItalianDate(dateIso);
    if (precise) lines.push(precise);
    if (extraLabel?.trim()) lines.push(extraLabel.trim());
    const normalized = email?.trim();
    if (normalized) lines.push(normalized);
    return lines;
  }, [dateIso, email, extraLabel]);

  useEffect(
    () => () => {
      clearTimeout(showTimer.current);
      clearTimeout(hideTimer.current);
    },
    [],
  );

  const handleEnter = () => {
    clearTimeout(hideTimer.current);
    clearTimeout(showTimer.current);
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
  };

  const handleLeave = () => {
    clearTimeout(showTimer.current);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), HIDE_DELAY_MS);
  };

  return (
    <div
      className={`wave-identity-tooltip-host${className ? ` ${className}` : ""}`}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
    >
      {children}
      {visible && tooltipLines.length > 0 && (
        <span className="wave-identity-tooltip-bubble" role="tooltip">
          {tooltipLines.map((line, index) => (
            <span
              key={`${index}-${line}`}
              className={index === 0 ? "wave-identity-tooltip-line-primary" : "wave-identity-tooltip-line-secondary"}
            >
              {line}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
